/**
 * SortableTaskRow — a task list row with a drag handle.
 *
 * Extracted from FocusView.jsx on 2026-04-08 so it can be reused by both
 * the Focus "My Tasks" list view AND the Focus "Board" kanban columns.
 *
 * Design:
 *   - useSortable() wires the row to a dnd-kit SortableContext.
 *   - Drag activation is on the grip handle ONLY (not the full card), so
 *     tapping the card body still opens the task modal via onClick.
 *   - Grip handle is always visibly present (opacity 0.85 default, opacity
 *     1 on hover/drag), navy on idle, accent on hover. This matches the
 *     CEO spec "handles must be unmistakable so everyone can organize
 *     their list" (feedback 2026-04-08 PM).
 *
 * Consumer contract:
 *   - Must be rendered inside a <SortableContext items={ids} ...>
 *   - Must be rendered inside a <DndContext> with appropriate sensors
 *   - Task IDs must be stable strings
 *   - activeId (from parent's drag state) suppresses the stagger-in
 *     animation while a drag is happening, so rows don't replay their
 *     entry animation when reordered.
 */
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import TaskCard from '../../components/shared/TaskCard';
import { useT } from '../../lib/i18n/I18nContext';

// Inject grip handle visibility CSS once per page load.
// Mobile: native press-and-hold drag already works via dnd-kit TouchSensor, so
// the explicit grip handle is dead weight AND steals horizontal space. Hide it
// below 768px and let the tap-and-hold gesture take over. Desktop keeps the
// visible handle because there's no equivalent gesture affordance.
const GRIP_STYLE_ID = 'sortable-task-row-grip';
if (typeof document !== 'undefined' && !document.getElementById(GRIP_STYLE_ID)) {
  const s = document.createElement('style');
  s.id = GRIP_STYLE_ID;
  s.textContent = `
    .sortable-task-row .grip-handle {
      opacity: 0.85;
      color: var(--navy) !important;
      transition: opacity 150ms, color 150ms;
    }
    .sortable-task-row:hover .grip-handle,
    .sortable-task-row.is-dragging .grip-handle {
      opacity: 1;
      color: var(--accent) !important;
    }
    @media (max-width: 767px) {
      .sortable-task-row .grip-handle-wrapper {
        display: none !important;
      }
    }
  `;
  document.head.appendChild(s);
}

export default function SortableTaskRow({ task, index, onClick, onCycleStatus, activeId, variant = 'focus' }) {
  const { t } = useT();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    zIndex: isDragging ? 50 : 'auto',
    marginBottom: 8,
    animation: activeId ? 'none' : `fadeUp 0.2s var(--ease) ${index * 30}ms both`,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`task-wrapper-${task.id}`}
      className={`sortable-task-row${isDragging ? ' is-dragging' : ''}`}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {/* Grip handle — drag activator only.
            listeners + attributes bind here (not on the parent) so a plain
            click on the card body still passes through to onClick.
            Wrapper class 'grip-handle-wrapper' lets the mobile media query
            hide it entirely (mobile uses dnd-kit TouchSensor press-and-hold). */}
        <div
          {...attributes}
          {...listeners}
          className="grip-handle-wrapper"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 44,
            minWidth: 28,
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            flexShrink: 0,
          }}
        >
          <GripVertical
            size={20}
            strokeWidth={2.25}
            className="grip-handle"
            aria-label={t('common.dragToReorder')}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <TaskCard
            task={task}
            variant={variant}
            onClick={onClick}
            onCycleStatus={onCycleStatus}
          />
        </div>
      </div>
    </div>
  );
}
